// W2A upper lane: session-bound runtime clients for screens 01-11 and the
// two Incoming companions. Co-located inside screens/CurrentActionReview/
// because W2A's lane excludes screens/_shared and apps/sales-workspace/src/api,
// and these screens all need the same tiny builder.
//
// Design:
//   - Reads the current HubSpot session (via useOptionalSession).
//   - When the session is `ready` AND the active identity is not the dev
//     mock (feedback: isDevMockSession) AND the explicit `?preview=sarah`
//     opt-in is NOT set, we mint real scoped clients that attach the
//     signed JWT + X-Customer-Id / X-Portal-Id headers via
//     SignedRuntimeApiClient (see apps/sales-workspace/src/api/transport.ts).
//   - Otherwise returns `null`. The caller treats null as "preview mode"
//     and keeps the illustrative fixture rendering W1B shipped.
//
// The three clients returned are:
//   - signed       : SignedRuntimeApiClient for raw GETs (reads).
//   - work         : WorkClient wrapping mutation / idempotent writes.
//   - composite    : MyActivityCompositeClient for the My Activity screen.
//
// This helper does NOT fetch anything itself. It only wires auth into the
// api/ factories W1A already shipped. Each screen owns its own fetch
// hook; the helper exists so no screen has to reinvent the session
// plumbing.

import { useMemo } from 'react';
import { useOptionalSession } from '../../auth/session-context.js';
import { isDevMockSession } from '../../auth/mockContext.js';
import { usePreviewFixtureMode } from '../_shared/useAuthenticatedCustomer.js';
import {
  createSignedRuntimeClient,
  type SignedRuntimeApiClient,
} from '../../api/transport.js';
import { createWorkClient, type WorkClient } from '../../api/work-client.js';
import {
  createMyActivityCompositeClient,
  type MyActivityCompositeClient,
} from '../../api/my-activity-composite.js';

export interface ScreenRuntimeClients {
  signed: SignedRuntimeApiClient;
  work: WorkClient;
  composite: MyActivityCompositeClient;
  baseUrl: string;
}

export function readRuntimeBaseUrl(): string {
  const injected =
    typeof window !== 'undefined'
      ? (window as { __RUNTIME_API_BASE_URL__?: string }).__RUNTIME_API_BASE_URL__
      : undefined;
  if (typeof injected === 'string' && injected.length > 0) return injected;
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  const fromEnv = env?.VITE_RUNTIME_API_BASE_URL;
  if (typeof fromEnv === 'string' && fromEnv.length > 0) return fromEnv;
  return '';
}

/**
 * Returns a bundle of session-scoped API clients, or `null` when the
 * screen should stay in preview-fixture mode. Preview mode wins whenever
 *   - the explicit `?preview=sarah` or `?preview=michael` query is set,
 *   - the dev mock session is active (local VITE_DEV_MOCK_CONTEXT),
 *   - the HubSpot handshake has not produced a `ready` session yet,
 *   - or no RUNTIME_API base URL is configured.
 */
export function useScreenRuntime(): ScreenRuntimeClients | null {
  const sessionCtx = useOptionalSession();
  const previewFlag = usePreviewFixtureMode();
  const devMock = isDevMockSession(sessionCtx?.session ?? null);
  const sessionReady =
    !!sessionCtx &&
    (sessionCtx.status === 'ready' || sessionCtx.status === 'refreshing') &&
    !!sessionCtx.session;
  // Capture a stable session accessor. Keyed by portalId + userId + token
  // so a token refresh re-mints the clients without churning identity.
  const sessionToken = sessionCtx?.session?.token ?? null;
  const sessionIdentity = sessionCtx?.session
    ? `${sessionCtx.session.portalId}:${sessionCtx.session.userId}`
    : null;
  const baseUrl = readRuntimeBaseUrl();

  return useMemo<ScreenRuntimeClients | null>(() => {
    if (!sessionReady || previewFlag || devMock) return null;
    if (!baseUrl) return null;
    const getSession = () => sessionCtx?.session ?? null;
    const getToken = () => sessionCtx?.session?.token ?? null;
    const getCustomerId = () => sessionCtx?.session?.context.objectId ?? null;
    const signed = createSignedRuntimeClient({ baseUrl, getSession });
    const work = createWorkClient({ baseUrl, getSession });
    const composite = createMyActivityCompositeClient({
      baseUrl,
      getToken,
      getCustomerId,
    });
    return { signed, work, composite, baseUrl };
    // sessionCtx is intentionally not in the deps: the accessor closures
    // read it on every call so a refresh picks up the latest session
    // without rebuilding the clients.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionReady, previewFlag, devMock, baseUrl, sessionIdentity, sessionToken]);
}
